import { Link } from '@tanstack/react-router';
import { CalendarPlus, Copy, Send } from 'lucide-react';
import { useState, type MouseEvent, type ReactNode } from 'react';
import { TEST_ACTION_LABELS, attemptStatusLabel, type ApplicationStage, type AssessmentAttemptStatus } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { Popover } from '../../../components/Popover';
import { ATTEMPT_STATUS_TONES, RESULT_TONES } from '../../../components/status-tones';
import type { Tone } from '../../../components/Tone';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { formatDateTime } from '../../../lib/format';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useApplicationTests } from '../api/assessments-api';
import { useResultsActions } from '../hooks/use-results-actions';
import { BookSessionPanel } from './BookSessionPanel';
import styles from './Assessments.module.css';

interface ApplicationTestsCardProps {
  readonly applicationId: string;
  readonly jobId: string;
  readonly candidateName: string;
  readonly stage: ApplicationStage;
  readonly version: number;
}

/**
 * CAND-03's test panel (S17): every attempt this application has had, and whatever is
 * due now — send, a new link, a retake (S17 Q8) — or "Copy link" while one is open
 * (Q3). Shown once the application has reached the test, or has ever been sent it.
 */
export function ApplicationTestsCard({ applicationId, jobId, candidateName, stage, version }: ApplicationTestsCardProps): ReactNode {
  const canView = useCanView('assessment_attempt');
  const canAct = useCanEdit('application');
  const tests = useApplicationTests(applicationId, canView);
  const actions = useResultsActions(jobId);
  const [booking, setBooking] = useState<AnchorRect | null>(null);

  if (!canView || !tests.data) return null;
  const { attempts, nextAction, testName, attemptsUsed, attemptsAllowed } = tests.data;
  if (attempts.length === 0 && stage !== 'assessment') return null;
  const open = attempts.find((attempt) => attempt.isLinkOpen) ?? null;

  return (
    <Card title="Test" meta={testName ? `${testName} · ${attemptsUsed} of ${attemptsAllowed} attempt${attemptsAllowed === 1 ? '' : 's'} used` : 'No test chosen for this job yet'}>
      <div className={styles.stack}>
        {attempts.length === 0 ? (
          <p className={styles.hint}>{tests.data.isOffline ? 'Not booked yet.' : 'Not sent yet.'}</p>
        ) : (
          <ul className={styles.list}>
            {attempts.map((attempt) => (
              <li key={attempt.id} className={styles.item}>
                <div className={styles.itemHead}>
                  <span className={styles.poolText}>Attempt {attempt.attemptNumber}</span>
                  <Chip tone={toneOf(attempt.status, attempt.didPass)} plain>
                    {attemptStatusLabel(attempt.status, attempt.examSession !== null)}
                  </Chip>
                  {attempt.scorePercent !== null && <strong>{attempt.scorePercent}%</strong>}
                </div>
                {attempt.examSession && (
                  <Link to="/assessments/sessions/$sessionId" params={{ sessionId: attempt.examSession.id }} className={styles.meta}>
                    {attempt.examSession.name}
                    {attempt.examSession.startsAt ? ` · ${formatDateTime(attempt.examSession.startsAt)}` : ''}
                    {attempt.examSession.venue ? ` · ${attempt.examSession.venue}` : ''}
                  </Link>
                )}
                <span className={styles.meta}>
                  {attempt.examSession ? 'Booked' : 'Sent'} {formatDateTime(attempt.sentAt)}
                  {attempt.submittedAt ? ` · submitted ${formatDateTime(attempt.submittedAt)}` : ''}
                  {attempt.isLinkOpen && attempt.expiresAt ? ` · link works until ${formatDateTime(attempt.expiresAt)}` : ''}
                  {attempt.hasPendingManualGrading && attempt.graderName ? ` · to be marked by ${attempt.graderName}` : ''}
                </span>
                {attempt.sections.length > 1 && attempt.scoreRaw !== null && (
                  <span className={styles.meta}>{attempt.sections.map((section) => `${section.name} ${section.scoreRaw ?? '—'}/${section.scoreMax ?? '—'}`).join(' · ')}</span>
                )}
              </li>
            ))}
          </ul>
        )}
        {canAct && (nextAction || open) && (
          <div className={styles.actionsRow}>
            {nextAction === 'book' && (
              <Button variant="solid" size="sm" icon={CalendarPlus} isPending={actions.pending.has(applicationId)} onClick={(event: MouseEvent<HTMLButtonElement>) => setBooking(anchorFrom(event.currentTarget))}>
                {TEST_ACTION_LABELS.book}
              </Button>
            )}
            {nextAction && nextAction !== 'book' && (
              <Button variant="solid" size="sm" icon={Send} isPending={actions.pending.has(applicationId)} onClick={() => void actions.sendTest({ applicationId, candidateName, version, nextAction })}>
                {TEST_ACTION_LABELS[nextAction]}
              </Button>
            )}
            {open && (
              <Button variant="outline" size="sm" icon={Copy} onClick={() => void actions.copy(open.id, candidateName)}>
                Copy link
              </Button>
            )}
          </div>
        )}
      </div>
      <Popover anchor={booking} onClose={() => setBooking(null)} align="left" width={420} label="Book a session">
        {booking && (
          <BookSessionPanel
            jobId={jobId}
            who={candidateName}
            needed={1}
            onCancel={() => setBooking(null)}
            onPick={(sessionId, sessionName) => {
              setBooking(null);
              void actions.book({ applicationId, candidateName }, sessionId, sessionName);
            }}
          />
        )}
      </Popover>
    </Card>
  );
}

function toneOf(status: AssessmentAttemptStatus, didPass: boolean | null): Tone {
  if (didPass === true) return RESULT_TONES.passed;
  if (didPass === false) return RESULT_TONES.below;
  return ATTEMPT_STATUS_TONES[status];
}
